import { useEffect, useMemo, useRef, useState } from 'react';
import type { CompetitionVideoAssetPublic } from '../services/api';

interface CompetitionVideoSurfaceProps {
  title: string;
  videoAsset?: CompetitionVideoAssetPublic | null;
  fallbackImageUrl?: string | null;
  preferVideo?: boolean;
  className?: string;
  priority?: boolean;
  showBadge?: boolean;
  badgeLabel?: string;
}

function supportsVideoPlayback(): boolean {
  if (typeof window === 'undefined') return false;
  const video = window.document.createElement('video');
  return typeof video.canPlayType === 'function';
}

export default function CompetitionVideoSurface({
  title,
  videoAsset,
  fallbackImageUrl,
  preferVideo = true,
  className = '',
  priority = false,
  showBadge = false,
  badgeLabel = 'Video preview',
}: CompetitionVideoSurfaceProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isInView, setIsInView] = useState(priority);
  const [videoFailed, setVideoFailed] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const [allowMotionVideo, setAllowMotionVideo] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (priority) {
      setIsInView(true);
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }
    const node = hostRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [priority]);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData =
      typeof navigator !== 'undefined' &&
      'connection' in navigator &&
      Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData);
    setAllowMotionVideo(!reduce && !saveData && supportsVideoPlayback());
  }, []);

  useEffect(() => {
    setVideoFailed(false);
    setIsPaused(false);
  }, [videoAsset?.video_url]);

  useEffect(() => {
    setPosterFailed(false);
  }, [videoAsset?.poster_url, fallbackImageUrl]);

  const shouldShowVideo = useMemo(
    () => Boolean(preferVideo && videoAsset && isInView && allowMotionVideo && !videoFailed),
    [allowMotionVideo, isInView, preferVideo, videoAsset, videoFailed],
  );

  const posterUrl = videoAsset?.poster_url ?? fallbackImageUrl ?? undefined;
  const imageUrl = preferVideo
    ? (posterFailed ? fallbackImageUrl ?? undefined : posterUrl ?? fallbackImageUrl ?? undefined)
    : (fallbackImageUrl ?? (posterFailed ? undefined : posterUrl) ?? undefined);
  const fallbackAlt = fallbackImageUrl ? title : `${title} media placeholder`;

  const togglePaused = () => {
    const nextPaused = !isPaused;
    setIsPaused(nextPaused);
    const video = videoRef.current;
    if (!video) return;
    if (nextPaused) {
      video.pause();
      return;
    }
    void video.play().catch(() => setVideoFailed(true));
  };

  return (
    <div ref={hostRef} className={`relative ${className}`}>
      {shouldShowVideo && videoAsset ? (
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          autoPlay={!isPaused}
          muted
          loop
          playsInline
          preload={priority ? 'metadata' : 'none'}
          poster={posterUrl}
          aria-label={`${title} video preview`}
          onError={() => setVideoFailed(true)}
        >
          <source src={videoAsset.video_url} type={videoAsset.mime_type} />
        </video>
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt={fallbackAlt}
          className="h-full w-full object-cover"
          loading={priority ? 'eager' : 'lazy'}
          onError={() => {
            if (videoAsset?.poster_url && imageUrl === videoAsset.poster_url && fallbackImageUrl) {
              setPosterFailed(true);
            }
          }}
        />
      ) : (
        <div
          aria-label={fallbackAlt}
          className="h-full w-full bg-gradient-to-br from-indigo-200 to-primary/30"
        />
      )}

      {showBadge && videoAsset && (
        <span className="absolute bottom-2 right-2 rounded-full bg-black/65 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
          {badgeLabel}
        </span>
      )}

      {shouldShowVideo && videoAsset && (
        <button
          type="button"
          onClick={togglePaused}
          className="absolute bottom-2 left-2 rounded-full bg-black/65 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur"
          aria-label={isPaused ? `Resume ${title} video preview` : `Pause ${title} video preview`}
        >
          {isPaused ? 'Resume video' : 'Pause video'}
        </button>
      )}
    </div>
  );
}
